import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Table,
  Typography,
  Divider,
  Modal,
  Form,
  message,
  Tag,
  Space,
  Select,
  InputNumber,
  Alert,
  Popconfirm
} from 'antd';
import { SearchOutlined, PlusOutlined, DeleteOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Product, productService } from '../../services/productService';
import { createOrder } from '../../services/orderService';
import { notificationService } from '../../services/notificationService';
import { zoneService, Zone } from '../../services/zoneService';
import { getLotSize } from '../../utils/lotSize';

const { Option } = Select;
const { Title, Text } = Typography;

interface CartItem {
  product: Product;
  quantity: number;
  price: number;
}

const CASHIER_DRAFT_KEY = 'hellogassy_cashier_draft';

interface CashierDraft {
  cart: CartItem[];
  selectedZoneId?: string;
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

const loadCashierDraft = (): CashierDraft | null => {
  try {
    const raw = localStorage.getItem(CASHIER_DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CashierDraft;
  } catch {
    return null;
  }
};

const saveCashierDraft = (draft: CashierDraft) => {
  try {
    localStorage.setItem(CASHIER_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota errors
  }
};

const clearCashierDraft = () => {
  localStorage.removeItem(CASHIER_DRAFT_KEY);
};

const emptyCustomer = {
  firstName: '',
  lastName: '',
  email: '',
  phone: ''
};

const CashierRegister: React.FC = () => {
  const draft = loadCashierDraft();
  const [products, setProducts] = useState<Product[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string | undefined>(draft?.selectedZoneId);
  const [cart, setCart] = useState<CartItem[]>(draft?.cart || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerModalVisible, setCustomerModalVisible] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(draft?.customerInfo || emptyCustomer);
  const restoredNotified = React.useRef(false);

  useEffect(() => {
    fetchProducts();
    fetchZones();
  }, []);

  // Sauvegarde auto du brouillon (panier + zone + client)
  useEffect(() => {
    if (cart.length === 0 && !selectedZoneId && !customerInfo.firstName && !customerInfo.lastName && !customerInfo.phone) {
      clearCashierDraft();
      return;
    }
    saveCashierDraft({ cart, selectedZoneId, customerInfo });
  }, [cart, selectedZoneId, customerInfo]);

  const fetchProducts = async () => {
    try {
      const data = await productService.getAllProducts();
      setProducts(data);

      // Remettre à jour le stock des produits déjà dans le panier
      setCart((prev) => {
        if (prev.length === 0) return prev;
        return prev
          .map((item) => {
            const fresh = data.find((p: Product) => p._id === item.product._id);
            if (!fresh) return null;
            const lotSize = getLotSize(fresh.price, fresh.lotSize);
            const maxQty = Math.floor(fresh.stock / lotSize);
            const quantity = Math.min(item.quantity, Math.max(maxQty, 0));
            if (quantity <= 0) return null;
            return {
              product: fresh,
              quantity,
              price: fresh.price * quantity * lotSize,
            };
          })
          .filter(Boolean) as CartItem[];
      });

      if (!restoredNotified.current && (draft?.cart?.length || 0) > 0) {
        restoredNotified.current = true;
        message.info(`Panier restauré (${draft!.cart.length} article${draft!.cart.length > 1 ? 's' : ''})`);
      }
    } catch (error) {
      message.error('Erreur lors du chargement des produits');
    }
  };

  const fetchZones = async () => {
    try {
      const data = await zoneService.getAllZones();
      setZones(data);
    } catch (error) {
      console.error('Erreur lors du chargement des zones');
    }
  };

  const selectedZone = zones.find(z => z._id === selectedZoneId);
  const shippingPrice = selectedZone ? selectedZone.price : 0;

  const addToCart = (product: Product) => {
    const existingItem = cart.find(item => item.product._id === product._id);
    const currentQuantity = existingItem ? existingItem.quantity : 0;
    const newQuantity = currentQuantity + 1;
    const lotSize = getLotSize(product.price, product.lotSize);

    // Vérification du stock disponible
    const stockCheck = newQuantity * lotSize <= product.stock;
    if (!stockCheck) {
      const maxQuantity = Math.floor(product.stock / lotSize);
      message.error(`Stock insuffisant. Maximum disponible: ${maxQuantity} ${lotSize > 1 ? 'lot(s)' : 'unité(s)'}`);
      return;
    }

    if (existingItem) {
      setCart(cart.map(item =>
        item.product._id === product._id
          ? {
              ...item,
              quantity: newQuantity,
              price: product.price * newQuantity * lotSize
            }
          : item
      ));
    } else {
      setCart([...cart, {
        product,
        quantity: 1,
        price: product.price * lotSize
      }]);
    }

    message.success(`${product.name} ajouté au panier`);
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const item = cart.find(item => item.product._id === productId);
    if (!item) return;

    const lotSize = getLotSize(item.product.price, item.product.lotSize);
    const stockCheck = quantity * lotSize <= item.product.stock;

    if (!stockCheck) {
      const maxQuantity = Math.floor(item.product.stock / lotSize);
      message.error(`Stock insuffisant. Maximum disponible: ${maxQuantity} ${lotSize > 1 ? 'lot(s)' : 'unité(s)'}`);
      return;
    }

    setCart(cart.map(item =>
      item.product._id === productId
        ? {
            ...item,
            quantity,
            price: item.product.price * quantity * lotSize
          }
        : item
    ));
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter(item => item.product._id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setSelectedZoneId(undefined);
    setCustomerInfo(emptyCustomer);
    clearCashierDraft();
    message.info('Panier vidé');
  };

  const getTotalQuantity = () => cart.reduce((sum, item) => sum + item.quantity, 0);
  const getTotalPrice = () => cart.reduce((sum, item) => sum + item.price, 0);
  const getGrandTotal = () => getTotalPrice() + shippingPrice;

  const handleCheckout = () => {
    if (cart.length === 0) {
      message.warning('Le panier est vide');
      return;
    }

    setCustomerModalVisible(true);
  };

  const handleCustomerSubmit = async () => {
    if (!customerInfo.firstName || !customerInfo.lastName || !customerInfo.phone) {
      message.error('Veuillez remplir les informations obligatoires');
      return;
    }

    try {
      setLoading(true);

      const itemsPrice = getTotalPrice();
      const orderData = {
        orderItems: cart.map(item => ({
          product: item.product._id,
          name: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          image: item.product.images[0]
        })),
        personalInfo: {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          email: customerInfo.email || 'caissier@magasin.com',
          phone: customerInfo.phone
        },
        deliveryMethod: (selectedZoneId ? 'delivery' : 'pickup') as 'pickup' | 'delivery',
        paymentMethod: 'cash',
        itemsPrice,
        shippingPrice,
        taxPrice: 0,
        totalPrice: itemsPrice + shippingPrice,
        ...(selectedZoneId ? { zoneId: selectedZoneId } : {})
      };

      await createOrder(orderData);
      message.success('Commande créée avec succès !');
      notificationService.playNotificationSound();
      setCart([]);
      setCustomerInfo(emptyCustomer);
      setSelectedZoneId(undefined);
      clearCashierDraft();
      setCustomerModalVisible(false);
    } catch (error) {
      message.error('Erreur lors de la création de la commande');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const cartColumns = [
    {
      title: 'Produit',
      dataIndex: 'product',
      key: 'product',
      render: (product: Product) => product.name,
    },
    {
      title: 'Prix unit.',
      dataIndex: 'product',
      key: 'price',
      render: (product: Product) => `${product.price.toLocaleString('fr-FR')} FCFA`,
    },
    {
      title: 'Quantité',
      key: 'quantity',
      render: (record: CartItem) => (
        <InputNumber
          min={1}
          value={record.quantity}
          onChange={(value) => updateQuantity(record.product._id, value || 1)}
          size="small"
        />
      ),
    },
    {
      title: 'Total',
      key: 'total',
      render: (record: CartItem) => {
        const lotSize = getLotSize(record.product.price, record.product.lotSize);
        return (
          <div>
            <div>{record.price.toLocaleString('fr-FR')} FCFA</div>
            {lotSize > 1 && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                ({record.quantity} lot{record.quantity > 1 ? 's' : ''} de {lotSize})
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: CartItem) => (
        <Popconfirm
          title="Retirer ce produit du panier ?"
          okText="Retirer"
          cancelText="Annuler"
          okButtonProps={{ danger: true }}
          onConfirm={() => removeFromCart(record.product._id)}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Caisse - Passage de commande</Title>

      <Row gutter={[24, 24]}>
        {/* Produits */}
        <Col xs={24} lg={16}>
          <Card
            title="Produits disponibles"
            extra={
              <Space>
                <Input
                  placeholder="Rechercher un produit..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: 250 }}
                />
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {filteredProducts.map(product => (
                <Col xs={12} sm={8} md={6} key={product._id}>
                  <Card
                    hoverable
                    size="small"
                    cover={
                      <img
                        alt={product.name}
                        src={product.images[0]}
                        style={{ height: 120, objectFit: 'cover' }}
                      />
                    }
                    actions={[
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => addToCart(product)}
                        disabled={product.stock === 0}
                        size="small"
                        danger={product.stock === 0}
                      >
                        {product.stock === 0 ? 'Rupture' : 'Ajouter'}
                      </Button>
                    ]}
                  >
                    <Card.Meta
                      title={product.name}
                      description={
                        <div>
                          <Text strong>{product.price.toLocaleString('fr-FR')} FCFA</Text>
                          <br />
                          <Tag color="blue">{product.category}</Tag>
                          <br />
                          <Tag
                            color={product.stock === 0 ? 'red' : product.stock <= 10 ? 'orange' : 'green'}
                          >
                            Stock: {product.stock}
                          </Tag>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        {/* Panier */}
        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <ShoppingCartOutlined />
                Panier ({getTotalQuantity()} articles)
              </Space>
            }
            extra={
              <Popconfirm
                title="Vider le panier ?"
                description="Tous les articles et les infos client seront effacés."
                okText="Vider"
                cancelText="Annuler"
                okButtonProps={{ danger: true }}
                onConfirm={clearCart}
                disabled={cart.length === 0}
              >
                <Button
                  type="text"
                  danger
                  disabled={cart.length === 0}
                >
                  Vider
                </Button>
              </Popconfirm>
            }
          >
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <ShoppingCartOutlined style={{ fontSize: 48, color: '#ccc' }} />
                <br />
                <Text type="secondary">Panier vide</Text>
              </div>
            ) : (
              <>
                <Table
                  dataSource={cart}
                  columns={cartColumns}
                  pagination={false}
                  size="small"
                  rowKey={(record) => record.product._id}
                />

                <Divider />

                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Zone de livraison (optionnel)
                  </Text>
                  <Select
                    showSearch
                    allowClear
                    placeholder="Rechercher une zone..."
                    value={selectedZoneId}
                    onChange={(value) => setSelectedZoneId(value)}
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    options={zones.map(zone => ({
                      value: zone._id,
                      label: `${zone.name} — ${zone.price.toLocaleString('fr-FR')} FCFA`,
                    }))}
                  />
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div>
                    <Text type="secondary">Sous-total: {getTotalPrice().toLocaleString('fr-FR')} FCFA</Text>
                  </div>
                  {shippingPrice > 0 && (
                    <div>
                      <Text type="secondary">Livraison: {shippingPrice.toLocaleString('fr-FR')} FCFA</Text>
                    </div>
                  )}
                  <Text strong>Total: {getGrandTotal().toLocaleString('fr-FR')} FCFA</Text>
                </div>

                {/* Alertes contextuelles */}
                {cart.some(item => getLotSize(item.product.price, item.product.lotSize) > 1) && (
                  <Alert
                    message="Information"
                    description="La quantité correspond au nombre de lots. La taille de chaque lot est indiquée sous l’article."
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleCheckout}
                  disabled={cart.length === 0}
                  style={{ marginTop: 16 }}
                >
                  Finaliser la commande
                </Button>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Modal client */}
      <Modal
        title="Informations du client"
        open={customerModalVisible}
        onOk={handleCustomerSubmit}
        onCancel={() => setCustomerModalVisible(false)}
        confirmLoading={loading}
        okText="Créer la commande"
        cancelText="Annuler"
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Prénom" required>
                <Input
                  value={customerInfo.firstName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                  placeholder="Prénom"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Nom" required>
                <Input
                  value={customerInfo.lastName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                  placeholder="Nom"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Téléphone" required>
                <Input
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  placeholder="77 123 45 67"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Email (optionnel)">
                <Input
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                  placeholder="email@exemple.com"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CashierRegister;
